import React from "react";
import {
  Box,
  useTheme,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const fieldNameMap = {
  id: "ID",
  ServiceID: "Service ID",
  ServiceId: "Service ID",

  Date: "Date",
  CheckingDate: "Checking Date",
  TimeStart: "Time Start",
  TimeEnd: "Time End",
  TotalDuration: "Total Duration",
  Duration: "Duration",
  ActualDuration: "Actual Duration",
  Deadline: "Deadline",

  Name: "Name",
  Details: "Details",

  TranslationStatus: "Translation Status",
  InterpretationStatus: "Interpretation Status",
  OtherServiceStatus: "Other Service Status",

  AccountCharge: "Account Charge",
  TotalCost: "Total Cost",
  AdditionalCost: "Additional Cost",
  LinguistPay: "Linguist Pay",
  Profit: "Profit",
  Margin: "Margin",

  TranslationType: "Translation Type",
  InterpretationType: "Interpretation Type",
  OtherServiceType: "Other Service Type",

  Mode: "Mode",
  Address: "Address",

  MileageRoundtrip: "Mileage Roundtrip",
  MileageCost: "Mileage Cost",
  ParkingFeesTolls: "Parking Fees / Tolls",
  PreparationTime: "Preparation Time",
  SchedulingTime: "Scheduling Time",

  ActualStart: "Actual Start",
  ActualEnd: "Actual End",

  Paid: "Paid",
  WordCount: "Word Count",
  NumberOfLanguages: "Number Of Languages",

  ContactPersonName: "Contact Person Name",
  ContactPersonNumber: "Contact Person Number",

  ClientRateDiscount: "Client Rate Discount",
  ServiceCategory: "Service Category",
  RateType: "Rate Type",
  NotarizationOrCOTCost: "Notarization / COT Cost",
  DeliveryType: "Delivery Type",
  PaymentType: "Payment Type",
  PaymentFees: "Payment Fees",
  DeliveryDate: "Delivery Date",
  DeliveryCost: "Delivery Cost",

  Deleted: "Deleted",
  Cancelled: "Cancelled",
  Invoiced: "Invoiced",
  CancellationReason: "Cancellation Reason",

  TargetLanguagesIDs: "Target Languages",

  ClientID: "Client",
  LinguistID: "Linguist",
  RequestorID: "Requestor",
  UnitTypeID: "Unit Type",
  CreatedBy: "Created By",
  CheckedBy: "Checked By",
  InvoiceTo: "Invoicer",

  Client: "Client",
  Requestor: "Requestor",
  Linguist: "Linguist",
  CheckedByMember: "Checked By",
  ChangedByMember: "Changed By",
  Source_Language: "Source Language",
  Service_Note: "Service Note",
  InvoiceToMember: "Invoicer",

  // Client fields
  Account: "Account Name",
  ShortName: "Short Name",
  MainAddress: "Main Address",
  OPIMinimumRate: "OPI Minimum Rate",
  Type: "Type",
  Note: "Note",
  "Service Types": "Service Types",
  "Payment Methods": "Payment Methods",
  "Requestor Added": "Requestor Added",
  "Requestor Deleted": "Requestor Deleted",
};

const noiseFields = new Set([
  "id",
  "createdAt",
  "updatedAt",
  "ServiceID",
  "graphEventId",
  "graphICalUId",
  "graphChangeKey",
  "graphLastSyncedAt",
]);

const parseChanges = (changes) => {
  if (!changes) return {};

  if (typeof changes === "string") {
    try {
      return JSON.parse(changes);
    } catch (error) {
      return {};
    }
  }

  if (typeof changes === "object") return changes;
  return {};
};

const valuesEqual = (left, right) => {
  if (left === right) return true;
  if (left == null && right == null) return true;

  if (typeof left === "object" && typeof right === "object" && left && right) {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch (error) {
      return false;
    }
  }

  return false;
};

const objectToDisplayValue = (value) => {
  if (!value || typeof value !== "object") return value;

  if (typeof value.Name === "string" && value.Name.trim() !== "") {
    return value.Name;
  }

  if (typeof value.name === "string" && value.name.trim() !== "") {
    return value.name;
  }

  if (value.Language && typeof value.Language.Name === "string") {
    return value.Language.Name;
  }

  return JSON.stringify(value);
};

const toDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (Array.isArray(value)) {
    if (!value.length) return "N/A";

    const normalized = value
      .map((item) => {
        if (item === null || item === undefined || item === "") return null;
        if (typeof item === "object") return objectToDisplayValue(item);
        return item;
      })
      .filter((item) => item !== null && item !== undefined && item !== "");

    return normalized.length ? normalized.join(", ") : "N/A";
  }

  if (typeof value === "object") return objectToDisplayValue(value);
  return value;
};

const toDisplayField = (field) => {
  if (fieldNameMap[field]) return fieldNameMap[field];

  if (field.startsWith("Requestor.")) {
    return field.replace("Requestor.", "Requestor ");
  }

  return field;
};

const shouldFormatAsDateField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized === "date" ||
    normalized.endsWith("date") ||
    normalized.includes(" date")
  );
};

const shouldFormatAsDurationField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized.includes("duration") ||
    (normalized.includes("time") && !normalized.includes("timestamp"))
  );
};

const shouldFormatAsTimeField = (field) => {
  const normalized = String(field || "").toLowerCase();
  return (
    normalized.includes("timestart") ||
    normalized.includes("timeend") ||
    normalized.includes("actualstart") ||
    normalized.includes("actualend")
  );
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const strValue = String(value);

  // Match HH:MM:SS or H:MM:SS format
  const match = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match) {
    const hours = match[1];
    const minutes = match[2];
    return `${hours}:${minutes}`;
  }

  return value;
};

const formatTime = (value) => {
  if (value === null || value === undefined || value === "") return value;

  // Try parsing as time (HH:MM:SS or HH:MM format)
  const strValue = String(value);
  const timeMatch = strValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2];
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes} ${period}`;
  }

  return value;
};

const formatDateValueForDisplay = (field, value) => {
  if (value === null || value === undefined || value === "") return value;

  // Check time fields first (more specific)
  if (shouldFormatAsTimeField(field)) {
    return formatTime(value);
  }

  // Check duration fields
  if (shouldFormatAsDurationField(field)) {
    return formatDuration(value);
  }

  // Check date fields
  if (shouldFormatAsDateField(field)) {
    const parsed = dayjs(value);
    if (!parsed.isValid()) return value;
    return parsed.format("MM-DD-YYYY");
  }

  return value;
};

const actionLabelMap = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  CHECK: "Checked",
};

const getActionIcon = (actionKey) => {
  switch (actionKey) {
    case "CREATE":
      return <AddIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "UPDATE":
      return <EditIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "DELETE":
      return <DeleteIcon sx={{ color: "white", fontSize: "14px" }} />;
    case "CHECK":
      return <CheckIcon sx={{ color: "white", fontSize: "14px" }} />;
    default:
      return <EditIcon sx={{ color: "white", fontSize: "14px" }} />;
  }
};

const getActionColor = (theme, actionKey) => {
  switch (actionKey) {
    case "CREATE":
      return theme.palette.success.main;
    case "UPDATE":
      return theme.palette.info.main;
    case "DELETE":
      return theme.palette.error.main;
    case "CHECK":
      return theme.palette.secondary.main;
    default:
      return theme.palette.primary.main;
  }
};

const DUPLICATE_WINDOW_MS = 90 * 60 * 1000;

const getEntryPairs = (entry) => {
  if (!Array.isArray(entry?.Content)) return [];

  return entry.Content.map(
    (row) => `${row.field}|${String(row.from)}|${String(row.to)}`,
  ).sort();
};

const isLegacyTargetLanguageCreate = (entry) => {
  if (entry?.Action !== "Created" || !Array.isArray(entry?.Content))
    return false;

  return (
    entry.Content.length === 1 &&
    (entry.Content[0].field === "TargetLanguage" ||
      entry.Content[0].field === "Target Languages")
  );
};

const isNearDuplicateHistoryEntry = (current, existing) => {
  if (!current || !existing) return false;
  if (current.Action !== existing.Action) return false;
  if (current.Member !== existing.Member) return false;
  if (!Array.isArray(current.Content) || !Array.isArray(existing.Content))
    return false;
  if (!current.Content.length || !existing.Content.length) return false;

  const currentTs = new Date(current.TimeStamp).getTime();
  const existingTs = new Date(existing.TimeStamp).getTime();
  if (!Number.isFinite(currentTs) || !Number.isFinite(existingTs)) return false;
  if (Math.abs(currentTs - existingTs) > DUPLICATE_WINDOW_MS) return false;

  const currentPairs = getEntryPairs(current);
  const existingPairs = getEntryPairs(existing);

  if (!currentPairs.length || !existingPairs.length) return false;

  const currentSet = new Set(currentPairs);
  const existingSet = new Set(existingPairs);

  let overlap = 0;
  currentSet.forEach((pair) => {
    if (existingSet.has(pair)) overlap += 1;
  });

  const smallestSize = Math.min(currentSet.size, existingSet.size);
  if (smallestSize === 0) return false;

  const overlapRatio = overlap / smallestSize;

  return overlap >= 3 && overlapRatio >= 0.6;
};

const isNoteField = (field = "") => {
  const normalized = String(field).trim().toLowerCase();
  return normalized === "note" || normalized === "service note";
};

const getHistoryTitle = (entry, entityType = "assignment") => {
  const member = entry?.Member || "N/A";
  const action = (entry?.Action || "Updated").toLowerCase();
  const content = Array.isArray(entry?.Content) ? entry.Content : [];

  const hasOnlyNoteChanges =
    content.length > 0 && content.every((row) => isNoteField(row?.field));

  if (hasOnlyNoteChanges) {
    if (entry?.Action === "Deleted") return `${member} removed a note`;

    const hasExistingNoteContent = content.some((row) => {
      const fromValue = String(row?.from ?? "")
        .trim()
        .toLowerCase();
      return fromValue !== "" && fromValue !== "n/a" && fromValue !== "null";
    });

    if (hasExistingNoteContent || entry?.Action === "Updated") {
      return `${member} edited a note`;
    }

    return `${member} added a note`;
  }

  return `${member} ${action} the ${entityType}`;
};

const transformData = (auditLogs = []) => {
  const normalized = auditLogs
    .map((item) => {
      const action = actionLabelMap[item.Action] || item.Action || "Updated";
      const changes = parseChanges(item.Changes);

      const content = Object.entries(changes)
        .filter(([field]) => !noiseFields.has(field))
        .map(([field, change]) => {
          const from = toDisplayValue(
            formatDateValueForDisplay(field, change?.from),
          );
          const to = toDisplayValue(
            formatDateValueForDisplay(field, change?.to),
          );

          if (valuesEqual(from, to)) return null;

          return {
            field: toDisplayField(field),
            from,
            to,
          };
        })
        .filter(Boolean);

      // If changes are empty for update/check actions, mimic old behavior and hide noise cards.
      if (
        (action === "Updated" || action === "Checked") &&
        content.length === 0
      ) {
        return null;
      }

      // Notes should still appear in the timeline as regular change rows (e.g., Note / Service Note fields).

      return {
        id: item.id,
        ActionKey: (item.Action || "UPDATE").toString().toUpperCase(),
        Action: action,
        Member: item.User?.Name || "N/A",
        TimeStamp: item.Timestamp,
        Content: content.length > 0 ? content : null,
        NoteTo: null,
      };
    })
    .filter(Boolean);

  const deduped = [];

  normalized.forEach((entry) => {
    if (isLegacyTargetLanguageCreate(entry)) return;

    const duplicateExists = deduped.some((existing) =>
      isNearDuplicateHistoryEntry(entry, existing),
    );

    if (!duplicateExists) {
      deduped.push(entry);
    }
  });

  return deduped;
};

const History = (props) => {
  const { serviceHistories, entityType = "assignment" } = props;

  const theme = useTheme();

  const transformedServiceHistories = transformData(serviceHistories || []);

  const noHistory = (
    <Paper
      elevation={1}
      sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}
    >
      <Typography color="text.secondary">
        No history recorded for this {entityType} yet, history will show once
        changes are made.
      </Typography>
    </Paper>
  );

  return (
    <Box height="100%" width="100%" p=".75rem 0.25rem">
      {transformedServiceHistories.length === 0 ? (
        noHistory
      ) : (
        <Box sx={{ position: "relative", pl: 4 }}>
          <Box
            sx={{
              position: "absolute",
              left: "16px",
              top: 0,
              bottom: 0,
              width: "2px",
              bgcolor: theme.palette.divider,
            }}
          />

          {transformedServiceHistories.map((singleHistory, index) => {
            const changesCount = singleHistory.Content?.length || 0;

            return (
              <Box
                key={singleHistory.id || index}
                sx={{ position: "relative", mb: 2 }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: "-32px",
                    top: "8px",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    bgcolor: getActionColor(theme, singleHistory.ActionKey),
                    border: `3px solid ${theme.palette.background.alt}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    zIndex: 1,
                  }}
                >
                  {getActionIcon(singleHistory.ActionKey)}
                </Box>

                <Accordion
                  elevation={2}
                  sx={{
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="600">
                            {getHistoryTitle(singleHistory, entityType)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(singleHistory.TimeStamp).fromNow()} •{" "}
                            {dayjs(singleHistory.TimeStamp).format(
                              "MMM DD, YYYY h:mm A",
                            )}
                            {changesCount > 0
                              ? ` • ${changesCount} field${changesCount > 1 ? "s" : ""}`
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Divider sx={{ mb: 2 }} />

                    {changesCount > 0 ? (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Changes:
                        </Typography>

                        {singleHistory.Content.map(({ field, from, to }) => (
                          <Box
                            key={`${singleHistory.id}-${field}-${String(from)}-${String(to)}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 1,
                              borderRadius: "12px",
                              bgcolor: theme.palette.action.hover,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="500"
                              sx={{ minWidth: "150px" }}
                            >
                              {field}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {String(from)}
                            </Typography>
                            <Typography variant="body2">→</Typography>
                            <Typography variant="body2" fontWeight="500">
                              {String(to)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No detailed changes available
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default History;
